import { useEffect, useState, useCallback } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { toast } from "sonner";
import { CreditCard, Trash2, Plus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

let stripePromise = null;

const DECLINE_IT = {
  live_mode_test_card: "Hai inserito una carta di test (es. 4242…): l'app è in modalità reale, usa una carta vera.",
  insufficient_funds: "Fondi insufficienti sulla carta.",
  lost_card: "La carta risulta smarrita: contatta la tua banca.",
  stolen_card: "La carta risulta rubata: contatta la tua banca.",
  expired_card: "La carta è scaduta.",
  incorrect_cvc: "Codice CVC non corretto.",
  incorrect_number: "Numero carta non corretto.",
  card_not_supported: "Questa carta non è supportata per il salvataggio: prova un'altra carta.",
  do_not_honor: "La banca ha rifiutato l'operazione (do not honor): contatta la banca o usa un'altra carta.",
  generic_decline: "La banca ha rifiutato la carta senza motivo specifico: prova un'altra carta o contatta la banca.",
  authentication_required: "La banca richiede l'autenticazione (3D Secure): riprova e completa la verifica.",
};

export function explainCardError(err) {
  const code = err?.decline_code || err?.code;
  const base = DECLINE_IT[code] || err?.message || "Carta rifiutata.";
  return isLiveMode() ? `${base} (modalità reale · codice: ${code || "n/d"})` : `${base} (codice: ${code || "n/d"})`;
}

let liveMode = false;
export const isLiveMode = () => liveMode;

const PENDING_KEY = "enb_card_setup";
const DIALOG_KEY = "enb_card_dialog";
const readPending = () => {
  try {
    const p = JSON.parse(localStorage.getItem(PENDING_KEY) || "null");
    return p && Date.now() - p.ts < 30 * 60 * 1000 ? p : null;
  } catch { return null; }
};

function AddCardForm({ onDone }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [resumeMsg, setResumeMsg] = useState(null);

  const finish = (ok, msg) => {
    localStorage.removeItem(PENDING_KEY);
    if (ok) { toast.success(msg || "Carta salvata"); onDone(); }
  };

  // Ripresa dopo ricaricamento pagina (es. rientro dall'app della banca per il 3D Secure)
  useEffect(() => {
    const pending = readPending();
    if (!stripe || !pending) return;
    (async () => {
      setBusy(true);
      try {
        const { setupIntent, error } = await stripe.retrieveSetupIntent(pending.client_secret);
        if (error || !setupIntent) { localStorage.removeItem(PENDING_KEY); return; }
        if (setupIntent.status === "succeeded") { finish(true, "Carta salvata: verifica completata"); return; }
        if (setupIntent.status === "requires_action" || setupIntent.status === "requires_confirmation") {
          setResumeMsg("Stavi salvando una carta: completa la verifica della banca.");
          const result = await stripe.confirmCardSetup(pending.client_secret);
          if (result.error) { setResumeMsg("Verifica non completata: reinserisci la carta per riprovare."); localStorage.removeItem(PENDING_KEY); return; }
          finish(true, "Carta salvata: verifica completata"); return;
        }
        setResumeMsg("Il salvataggio precedente non è andato a buon fine: reinserisci la carta.");
        localStorage.removeItem(PENDING_KEY);
      } finally { setBusy(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe]);

  const submit = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    try {
      const { data } = await api.post("/payments/save-card");
      localStorage.setItem(PENDING_KEY, JSON.stringify({ client_secret: data.client_secret, ts: Date.now() }));
      const result = await stripe.confirmCardSetup(data.client_secret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (result.error) { localStorage.removeItem(PENDING_KEY); toast.error(explainCardError(result.error), { duration: 8000 }); setBusy(false); return; }
      finish(true, "Carta salvata");
    } catch (err) {
      localStorage.removeItem(PENDING_KEY);
      const d = err.response?.data?.detail;
      toast.error(d === "CONNECT_REQUIRED" ? "Prima collega il tuo conto Stripe, poi potrai aggiungere la carta." : apiError(d));
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="border border-neutral-300 rounded-xl p-3">
        <CardElement options={{ style: { base: { fontSize: "16px", color: "#0a0a0a" } } }} />
      </div>
      {resumeMsg && <p data-testid="card-resume-msg" className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-xl p-3">{resumeMsg}</p>}
      <p data-testid="card-mode-hint" className="text-xs text-neutral-500">
        {isLiveMode() ? "Modalità reale: inserisci una carta vera (le carte di test come 4242… vengono rifiutate). Potrebbe essere richiesta la verifica 3D Secure della tua banca." : "Modalità test: usa la carta 4242 4242 4242 4242, scadenza futura, CVC qualsiasi."}
      </p>
      <Button data-testid="save-card-submit" onClick={submit} disabled={busy || !stripe} className="w-full h-11 rounded-full bg-black text-white">
        {busy ? "Salvo…" : "Salva carta"}
      </Button>
    </div>
  );
}

export default function CardManager({ t = (k) => k }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [methods, setMethods] = useState([]);
  const [open, setOpenState] = useState(() => localStorage.getItem(DIALOG_KEY) === "1" || !!readPending());
  const [ready, setReady] = useState(false);
  const [connectReady, setConnectReady] = useState(null);
  useEffect(() => {
    if (isAdmin) { setConnectReady(true); return; }
    api.get("/connect/status").then((r) => setConnectReady(!!r.data.ready)).catch(() => setConnectReady(false));
    const h = (e) => setConnectReady(!!e.detail);
    window.addEventListener("connect-changed", h);
    return () => window.removeEventListener("connect-changed", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const setOpen = (v) => { setOpenState(v); if (v) localStorage.setItem(DIALOG_KEY, "1"); else { localStorage.removeItem(DIALOG_KEY); localStorage.removeItem(PENDING_KEY); } };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/payments/methods");
      setMethods(data);
      window.dispatchEvent(new CustomEvent("cards-changed", { detail: data.length }));
    } catch {}
  }, []);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/payments/config");
      liveMode = (data.publishable_key || "").startsWith("pk_live");
      if (data.publishable_key && !stripePromise) stripePromise = loadStripe(data.publishable_key);
      setReady(true);
    })();
    load();
  }, [load]);

  const remove = async (pmId) => {
    try { await api.delete(`/payments/methods/${pmId}`); toast.success("Carta eliminata"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <h2 className="font-display font-bold mb-2 flex items-center gap-2"><CreditCard size={18} />{t("payment_methods")}</h2>
      <div className="space-y-2">
        {methods.length === 0 && <p className="text-xs text-neutral-400">{t("no_cards")}</p>}
        {methods.map((m) => (
          <div key={m.id} data-testid={`card-${m.id}`} className="flex items-center justify-between border border-neutral-200 rounded-xl p-3">
            <div className="flex items-center gap-3">
              <CreditCard size={18} />
              <span className="text-sm capitalize">{m.brand} •••• {m.last4}</span>
              <span className="text-xs text-neutral-400">{String(m.exp_month).padStart(2, "0")}/{m.exp_year}</span>
            </div>
            <button data-testid={`delete-card-${m.id}`} onClick={() => remove(m.id)} className="text-neutral-300 hover:text-red-500 p-1"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      {connectReady === false && (
        <p data-testid="card-needs-connect" className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-xl p-3">
          Per aggiungere una carta devi prima collegare il tuo conto Stripe (Passo 1, qui sopra).
        </p>
      )}
      <Button data-testid="add-card-btn" onClick={() => setOpen(true)} disabled={connectReady === false} variant="outline" className="w-full mt-3 h-11 rounded-full disabled:opacity-50"><Plus size={16} className="mr-1" />{t("add_card")}</Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display">{t("add_card")}</DialogTitle></DialogHeader>
          {ready && stripePromise ? (
            <Elements stripe={stripePromise}>
              <AddCardForm onDone={() => { setOpen(false); load(); }} />
            </Elements>
          ) : <p className="text-sm text-neutral-400">Caricamento…</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
